// Dynamic 24-hour forecast battery optimizer
// Finds the BEST 4 buy/sell pairs per day based on actual price spreads

class BatteryOptimizer {
  constructor() {
    this.batteryCapacity = 3.0; // MWh
    this.chargeEfficiency = 0.95;
    this.dischargeEfficiency = 0.95;
    this.roundTripEfficiency = 0.9025; // 0.95 * 0.95 = 90.25%
    this.tsoFee = 135; // DKK/MWh for grid electricity
  }

  // Find the 4 MOST PROFITABLE cycles in a 24-hour period
  findOptimalHours(dayPrices, solarHours = null) {
    if (!dayPrices || dayPrices.length !== 24) return [];

    // Build ALL profitable pairs using dynamic solar+grid mix for charging
    const pairs = [];

    for (let chargeHour = 0; chargeHour < 24; chargeHour++) {
      const chargePriceRaw = dayPrices[chargeHour]?.price ?? 0;
      const chargePrice = Math.max(0, chargePriceRaw);

      // Available solar energy (MWh) in charge hour
      const availableSolarMWh = solarHours && Array.isArray(solarHours) ? Math.max(0, solarHours[chargeHour] || 0) : 0;

      const maxChargeMWh = this.batteryCapacity; // 3.0 MWh
      const solarUsedMWh = Math.min(maxChargeMWh, availableSolarMWh);
      const gridUsedMWh = Math.max(0, maxChargeMWh - solarUsedMWh);

      // Mixed unit price for charging (before efficiency)
      const unitChargePriceMixed = (
        (solarUsedMWh * chargePrice) +
        (gridUsedMWh * (chargePrice + this.tsoFee))
      ) / Math.max(1e-9, maxChargeMWh);

      for (let dischargeHour = chargeHour + 1; dischargeHour < 24; dischargeHour++) {
        const dischargePrice = dayPrices[dischargeHour]?.price || 0;

        const chargeCost = (unitChargePriceMixed / this.chargeEfficiency) * maxChargeMWh;
        const dischargeRevenue = (dischargePrice * this.dischargeEfficiency) * maxChargeMWh;
        const profit = dischargeRevenue - chargeCost;
        const profitPerMWh = profit / maxChargeMWh;

        if (profit > 0.0001) {
          pairs.push({
            chargeHour,
            dischargeHour,
            chargeUnitPrice: unitChargePriceMixed,
            dischargePrice,
            profit,
            profitPerMWh,
            duration: dischargeHour - chargeHour,
            solarShare: solarUsedMWh / maxChargeMWh
          });
        }
      }
    }

    if (pairs.length === 0) return [];

    // Weighted interval scheduling to avoid overlaps (strictly increasing intervals)
    pairs.sort((a, b) => a.dischargeHour - b.dischargeHour);

    const p = new Array(pairs.length).fill(-1);
    for (let i = 0; i < pairs.length; i++) {
      p[i] = -1;
      for (let j = i - 1; j >= 0; j--) {
        if (pairs[j].dischargeHour < pairs[i].chargeHour) {
          p[i] = j;
          break;
        }
      }
    }

    const dp = new Array(pairs.length).fill(0);
    const take = new Array(pairs.length).fill(false);
    for (let i = 0; i < pairs.length; i++) {
      const includeProfit = pairs[i].profit + (p[i] >= 0 ? dp[p[i]] : 0);
      const excludeProfit = i > 0 ? dp[i - 1] : 0;
      if (includeProfit > excludeProfit + 1e-9) {
        dp[i] = includeProfit;
        take[i] = true;
      } else {
        dp[i] = excludeProfit;
        take[i] = false;
      }
    }

    const chosen = [];
    let i = pairs.length - 1;
    while (i >= 0) {
      if (take[i]) { chosen.push(pairs[i]); i = p[i]; }
      else { i = i - 1; }
    }
    chosen.reverse();

    const limited = chosen.slice(0, 4);
    return limited
      .map(pair => ({
        chargeHour: pair.chargeHour,
        chargePrice: pair.chargeUnitPrice,
        dischargeHour: pair.dischargeHour,
        dischargePrice: pair.dischargePrice,
        expectedProfit: pair.profitPerMWh,
        totalProfit: pair.profit,
        usesSolar: pair.solarShare > 0.01
      }))
      .sort((a, b) => a.chargeHour - b.chargeHour);
  }

  // Simplified decision making
  shouldChargeOrDischarge(hourOfDay, dayPrices, currentBatteryLevel,
                          hasChargedCycle1, hasDischargedCycle1,
                          hasChargedCycle2, hasDischargedCycle2,
                          hasChargedCycle3, hasDischargedCycle3,
                          hasChargedCycle4, hasDischargedCycle4,
                          solarHours = null) {

    const cycles = this.findOptimalHours(dayPrices, solarHours);

    if (!cycles || cycles.length === 0) return { action: 'none' };

    // Process each cycle in order
    for (let i = 0; i < cycles.length; i++) {
      const cycle = cycles[i];
      const cycleNum = i + 1;

      // Check if we've processed this cycle
      const hasCharged = [
        hasChargedCycle1,
        hasChargedCycle2,
        hasChargedCycle3,
        hasChargedCycle4
      ][i] || false;

      const hasDischarged = [
        hasDischargedCycle1,
        hasDischargedCycle2,
        hasDischargedCycle3,
        hasDischargedCycle4
      ][i] || false;

      // CHARGE: Charge when scheduled
      if (hourOfDay === cycle.chargeHour && !hasCharged) {
        // Calculate how much we can charge
        const spaceAvailable = 3.0 - currentBatteryLevel;

        // Charge even for partial space to enable smaller cycles
        if (spaceAvailable > 0.1) {
          return {
            action: 'charge',
            amount: Math.min(3.0, spaceAvailable), // Charge what we can
            cycle: cycleNum,
            price: cycle.chargePrice,
            usesSolar: cycle.usesSolar
          };
        }
      }

      // DISCHARGE: Discharge when scheduled
      if (hourOfDay === cycle.dischargeHour && hasCharged && !hasDischarged) {
        // Discharge if we have ANY meaningful charge
        if (currentBatteryLevel > 0.1) {
          return {
            action: 'discharge',
            amount: currentBatteryLevel, // Discharge everything
            cycle: cycleNum,
            price: cycle.dischargePrice
          };
        }
      }
    }

    // FALLBACK: If battery is nearly full and we haven't discharged, force discharge at good price
    if (currentBatteryLevel > 2.8 && hourOfDay >= 16 && hourOfDay <= 20) {
      // Evening peak hours - discharge even if not optimal
      const currentPrice = dayPrices[hourOfDay]?.price || 0;
      if (currentPrice > 300) { // Reasonable price threshold
        return {
          action: 'discharge',
          amount: currentBatteryLevel,
          cycle: 1, // Mark as cycle 1 for tracking
          price: currentPrice
        };
      }
    }

    return { action: 'none' };
  }
}

export default BatteryOptimizer;